import React, {useCallback, useState} from 'react';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import {MatrixMembership} from '@/types/matrix';
import {Box, Button, ListItem, ListItemText} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useMatrixContext} from '@/contexts/matrix/client';

interface PendingActionProps {
  status: MatrixMembership;
  topic: string;
  roomId: string;
}

const PendingAction: React.FC<PendingActionProps> = ({
  status,
  topic,
  roomId
}) => {
  const {t} = useTranslation();
  const {matrixClient} = useMatrixContext();
  const [isSending, setIsSending] = useState<boolean>(false);

  const label =
    status === MatrixMembership.INVITE
      ? t('pending_invite')
      : t('pending_knock');

  const onJoinRoom = useCallback(() => {
    if (matrixClient) {
      setIsSending(true);
      matrixClient.joinRoom(roomId).finally(() => {
        setIsSending(false);
      });
    }
  }, [matrixClient, roomId]);

  const onRejectInvite = useCallback(() => {
    if (matrixClient) {
      setIsSending(true);
      matrixClient.leave(roomId).finally(() => {
        setIsSending(false);
      });
    }
  }, [matrixClient, roomId]);

  const onRevokeKnock = useCallback(() => {
    if (matrixClient) {
      setIsSending(true);
      matrixClient.leave(roomId).finally(() => {
        setIsSending(false);
      });
    }
  }, [matrixClient, roomId]);

  return (
    <ListItem>
      <ListItemText
        primary={
          <Box>
            <ResponsiveTypography
              sx={{
                fontWeight: 'bold'
              }}
            >
              {topic}
            </ResponsiveTypography>
            <ResponsiveTypography>{label}</ResponsiveTypography>
          </Box>
        }
        secondary={
          <Box display="flex" alignItems="center" marginTop="2">
            {status === MatrixMembership.INVITE && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  sx={{
                    marginRight: 5
                  }}
                  onClick={onRejectInvite}
                  disabled={isSending}
                >
                  {t('reject_invite')}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={onJoinRoom}
                  disabled={isSending}
                >
                  {t('accept_invite')}
                </Button>
              </>
            )}
            {status === MatrixMembership.KNOCK && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={onRevokeKnock}
                disabled={isSending}
              >
                {t('revoke_knock')}
              </Button>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

export default PendingAction;
